import { type ReactNode, useContext } from "react";
import type { RowData } from "@tanstack/react-table";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import type {
    DataGridCellContext,
    DataGridCellTooltip,
} from "../columns/types";
import { CellTooltipHostContext } from "./CellTooltipHost";
import {
    type CellTooltipRegistry,
    CellTooltipRegistryContext,
    toCellTooltipBody,
} from "./cellTooltipRegistry";

interface CellTooltipTargetProps {
    /** Shown on hover and keyboard focus, cut off or not; `undefined` shows nothing. */
    tooltip: DataGridCellTooltip | undefined;
    children: ReactNode;
}

/** The attributes the host finds a target by; a structured tooltip registers itself. */
const toTargetProps = ({
    tooltip,
    registry,
}: {
    tooltip: DataGridCellTooltip | undefined;
    registry: CellTooltipRegistry;
}) => {
    if (tooltip === undefined) {
        return {};
    }
    if (typeof tooltip === "string") {
        return {
            "data-cell-tooltip": tooltip,
            "data-cell-tooltip-always": "",
        };
    }
    return {
        ref: (element: HTMLSpanElement) => {
            registry.set(element, tooltip);
            return () => registry.delete(element);
        },
        "data-cell-tooltip": "",
        "data-cell-tooltip-always": "",
        "data-cell-tooltip-rich": "",
    };
};

/**
 * Anchors a tooltip to its children alone: plain text or a title, sections
 * and bullets, shown whether or not anything is cut off. Inside a
 * `CellTooltipHost` the table's shared tooltip draws it; elsewhere (content
 * portaled out of the table) it mounts its own. The wrapper is a plain
 * inline span so its content lays out as without it: an inline-flex box
 * moves icons off the line's center.
 */
export const CellTooltipTarget = ({
    tooltip,
    children,
}: Readonly<CellTooltipTargetProps>) => {
    const hasTooltipHost = useContext(CellTooltipHostContext);
    const registry = useContext(CellTooltipRegistryContext);
    if (!hasTooltipHost || registry === null) {
        return (
            <Tooltip {...toCellTooltipBody(tooltip)}>
                <span>{children}</span>
            </Tooltip>
        );
    }
    return <span {...toTargetProps({ tooltip, registry })}>{children}</span>;
};

interface RowCellTooltipTargetProps<TData extends RowData> {
    context: DataGridCellContext<TData>;
    tooltip: (row: TData) => DataGridCellTooltip | undefined;
    children: ReactNode;
}

/** A column's `tooltip` for its row over the cell's content; grouping rows show none. */
export const RowCellTooltipTarget = <TData extends RowData>({
    context: { row },
    tooltip,
    children,
}: Readonly<RowCellTooltipTargetProps<TData>>) => (
    <CellTooltipTarget
        tooltip={row.getIsGrouped() ? undefined : tooltip(row.original)}
    >
        {children}
    </CellTooltipTarget>
);
